import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * The way out of the captive mini browser.
 *
 * ─── WHAT THIS IS FOR ───
 * A suspended customer reaches their bill because the walled garden redirected
 * them, and a phone opens that redirect in its CAPTIVE ASSISTANT — the little
 * sign-in window for coffee-shop wifi, not a real browser. Ray measured it
 * exactly, 2026-09-03: *"when tried to browser it works. also in messenger
 * browser it works, only in captive."*
 *
 * 🔴 THAT WINDOW BLOCKS FILE PICKERS AND DOWNLOADS ON PURPOSE, and reports
 * nothing when it does — which is why "Choose a photo" and "Save PDF" both did
 * nothing there while working everywhere else. No amount of markup fixes it.
 * The only real answer is to move the customer to their own browser, so this
 * gives them the address and one tap to do it.
 *
 * Shown to everyone, because a customer cannot be asked to know which kind of
 * window they are in — and it costs nothing to somebody already in Chrome.
 */

const IspPortalDiag: React.FC = () => {
   const [copied, setCopied] = useState(false);
   const url = typeof window !== 'undefined' ? window.location.href : '';

   const copy = async () => {
      try {
         await navigator.clipboard.writeText(url);
         setCopied(true);
      } catch {
         // A captive window may have no clipboard either. The address is on
         // screen and selectable, which is why it is printed in full.
         setCopied(false);
      }
   };

   /**
    * Android honours an intent: URL by handing the page to the default
    * browser. Everywhere else this is a normal link — harmless if the phone
    * ignores the scheme, and the address below is the fallback either way.
    */
   const openInBrowser = () => {
      try {
         const u = new URL(url);
         window.location.href = `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;end`;
      } catch {
         /* the address is printed below */
      }
   };

   return (
      <Panel>
         <Head>Can&apos;t choose a photo, or save your bill?</Head>
         <Body>
            You opened this from the wifi sign-in window, which cannot reach
            your photos. Open the same page in Chrome or Safari and it will
            work.
         </Body>
         <Url onClick={copy}>{url}</Url>
         <Row>
            <Ghost type="button" onClick={copy}>
               {copied ? 'Address copied' : 'Copy address'}
            </Ghost>
            <Solid type="button" onClick={openInBrowser}>
               Open in browser
            </Solid>
         </Row>
      </Panel>
   );
};

const Panel = styled.div`
   margin: 14px 0 0;
   padding: 14px;
   border: 1px solid #fcd34d;
   border-left: 4px solid #f59e0b;
   border-radius: 12px;
   background: #fffbeb;
   text-align: left;
`;
const Head = styled.div`
   font-size: 14px;
   font-weight: 800;
   color: #92400e;
   margin-bottom: 4px;
`;
const Body = styled.p`
   margin: 0 0 10px;
   font-size: 13px;
   line-height: 1.5;
   color: #92400e;
`;
const Url = styled.div`
   font-size: 11px;
   word-break: break-all;
   color: ${colors.GRAY5};
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 8px 10px;
   user-select: all;
   cursor: pointer;
`;
const Row = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 10px;
`;
const btn = `
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 9px 14px;
   border-radius: 9px;
   cursor: pointer;
`;
const Ghost = styled.button`
   ${btn}
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY5};
`;
const Solid = styled.button`
   ${btn}
   border: none;
   background: #b45309;
   color: #fff;
`;

export default IspPortalDiag;
